import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState, store } from "../../store/store";
import { subscribeToHost } from "../../../utils/helpers/EventsHelper";
import {
  CallbackElaborationsSnapshotStatus,
  CallbackElaborationStatus,
  CallbackHeartbeatStatus,
  CallbackSingleElaborationItemStatus,
  createElaborationRequest,
  ElabResultType,
} from "../../../api/object/RefreshObject";
import NotificationManager from "../../../common/notification/NotificationManager";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { handleState } from "../../../utils/helpers/ElaborationHelper";
import PanelStandaloneManager from "../../../common/panelStandaloneModal/PanelStandaloneManager";

const MAX_RETRIES = 5;

export interface ElaborationStatus {
  correlationId: string;
  clientId: string;
  documentId: string;
  userId: string;
  items: ElaborationStatusItem[];
}

export interface ElaborationStatusItem {
  id: string;
  contentControlId: string;
  status:
    | "REQUESTED"
    | "STARTED"
    | "REJECTED"
    | "REJECTED_FOR_CONCURRENCY"
    | "FAILED"
    | "COMPLETED"
    | "REJECTED_FOR_OBJECT_NOT_FOUND"
    | "ABORTED";
  startDate: string;
  endDate: string;
  elabResult: string;
  elabResultType: ElabResultType;
  originalElabResult?: string;
  correlationId: string;
  formSheetAreaId: ElaborationFormSheetArea;
  resultAdded: boolean;
  userChecked: boolean;
  messages: ElaborationMessage[];
  elaborationType?:
    | "ADD"
    | "EDIT"
    | "REFRESH"
    | "REFRESH_COMPONENT_INSTANCE"
    | "EXPORT"
    | "IMPORT"
    | "DUPLICATE"
    | "CHANGE_DOC_STRUCTURE"
    | "DETACH"
    | "CLONE"
    | "DOWNLOAD_COPY"
    | "SEND_TO_INDESIGN"
    | "RENAME"
    | "ALIGN_METADATA"
    | "CHECK_DIFFERENCES"
    | "SHEET_AND_AREA"
    | "EXPORT_OBJECT_LIST"
    | "EXPORT_SUBMISSION_HISTORY"
    | "DB_MAPPING"
    | "DOCUMENT_DB_LIST"
    | "CHANGE_TEMPLATE"
    | "COMPONENT_MAPPING";
  elaborationUpdateType?: "SINGLE" | "MULTIPLE";
  objectName: string;
  documentPartIncludedId?: string;
  inputRequest?: string;
  outputResponse?: string;
}

export interface AddedElaborationItem {
  correlationId: string;
  itemId: string;
}

export function elaborationStatusItemIsRunning(item: ElaborationStatusItem) {
  return item?.status === "STARTED" || item?.status === "REQUESTED";
}

export function elaborationStatusItemIsNotRunningAndNotUserChecked(item: ElaborationStatusItem) {
  return !elaborationStatusItemIsRunning(item) && !elaborationStatusItemIsUserChecked(item);
}

export function elaborationStatusItemIsToAdd(item: ElaborationStatusItem) {
  return item?.status === "COMPLETED" && !item.resultAdded;
}

export function elaborationStatusItemIsAdded(item: ElaborationStatusItem) {
  return item.status === "COMPLETED" && item.resultAdded;
}

export function elaborationStatusItemIsToHandleAbort(item: ElaborationStatusItem) {
  return item?.status === "ABORTED" && !item.resultAdded;
}

export function elaborationStatusItemIsCompleted(item: ElaborationStatusItem) {
  return item.status === "COMPLETED";
}

export function elaborationStatusItemIsRejected(item: ElaborationStatusItem) {
  return item.status === "REJECTED" || item.status === "REJECTED_FOR_CONCURRENCY";
}

export function elaborationStatusItemIsAborted(item: ElaborationStatusItem) {
  return item.status === "ABORTED" && !elaborationStatusItemIsUserChecked(item);
}

export function elaborationStatusItemIsEndButNotCompleted(item: ElaborationStatusItem) {
  return item.status === "FAILED";
}

export function elaborationStatusItemIsNotFound(item: ElaborationStatusItem) {
  return item.status === "REJECTED_FOR_OBJECT_NOT_FOUND";
}

export function elaborationStatusItemIsUserChecked(item: ElaborationStatusItem) {
  return item.userChecked;
}

export function elaborationStatusItemHasWarning(item: ElaborationStatusItem) {
  return item.messages?.filter((i) => i.severity === "WARNING").length > 0;
}

export const elaborationStatusItemIsBlocking = (item: ElaborationStatusItem) => {
  return (
    elaborationStatusItemIsRunning(item) ||
    (elaborationStatusItemIsToAdd(item) && !elaborationStatusItemIsUserChecked(item))
  );
};

export interface ElaborationFormSheetArea {
  dbId: string;
  formId: string;
  sheet: string;
  area: string;
}

export interface ElaborationMessage {
  text: string;
  details: string;
  severity: string;
  traceId: string;
}

export interface OpenEventChannelInput {
  baseUrl: string;
  documentId: string;
  userId: string;
  clientId: string;
}

// State and selectors ---
export interface RemoteElaborationsState {
  elaborationStatusList: ElaborationStatus[];
  lastReceivedEventTimestampMs?: number; //as unix timestamp
  hasElaboratedAtLeastOnce: boolean;
  activeEventChannelId?: string;
}

const initialState: RemoteElaborationsState = {
  elaborationStatusList: undefined,
  lastReceivedEventTimestampMs: undefined,
  hasElaboratedAtLeastOnce: false,
  activeEventChannelId: undefined,
};

export const createElaborationRequestAsync = createElaborationRequest;

const SEE_NOTIFICATION_KEY = "SEE_NOTIFICATION_KEY";

let retries = 0;

export const openEventChannelAsync = createAsyncThunk<any, OpenEventChannelInput>(
  "remoteElaborations/openEventChannel",
  (input, { dispatch }) => {
    dispatch(setActiveEventChannelId(input.documentId));
    subscribeToHost({
      paramPath: `${input.baseUrl}/elaboration/status/${input.userId}/${input.documentId}/${input.clientId}`,
      openCallback: () => {
        console.log(`sse opened`);
      },
      messageCallback: (
        message: CallbackElaborationStatus | CallbackElaborationsSnapshotStatus | CallbackSingleElaborationItemStatus
      ) => {
        try {
          console.log("received message", message);
          dispatch(updateElaboration(message));
          retries = 0;
          handleState();
        } catch (e) {
          console.log(`error handling message ${e.message}`);
        }
      },
      errorCallback: (e) => {
        retries++;
        if (store.getState().remoteElaborations.hasElaboratedAtLeastOnce && retries >= MAX_RETRIES) {
          PanelStandaloneManager.addPanelStandalone({
            key: "sse_reconnect_failure",
            modalTitle: "",
            message: formatMessage("cof.base.generalexception.message"),
            icon: "/assets/GeneralException.svg",
            type: "delete",
            actions: [
              {
                id: "refresh_button",
                title: formatMessage("cof.base.refresh"),
                onClick: () => location.reload(),
                variant: "blue",
              },
            ],
          });
        }
        dispatch(resetStatusList());
        console.log(`received sse completion/error`, e);
        if (e.type === "error") {
          switch (e.message) {
            case "Failed to fetch":
              break;
            case "Failed to parse data":
              addNotification("cof.base.message.parsererror", "cof.base.message.detail.parsererror", "error");
              break;
            default:
              console.log("Error on callback " + e.message);
          }
        }
      },
    });
  }
);

const addNotification = (title: string, message: string, type: "success" | "error" | "warning" | "info") => {
  NotificationManager.addNotification({
    title: formatMessage(title),
    message: formatMessage(message),
    key: SEE_NOTIFICATION_KEY,
    type: type,
    closable: true,
    removeOnClose: true,
    isTemporary: false,
  });
};

type CallbackMessage =
  | CallbackElaborationStatus
  | CallbackElaborationsSnapshotStatus
  | CallbackSingleElaborationItemStatus
  | CallbackHeartbeatStatus;

function isSnapshot(message: CallbackMessage): message is CallbackElaborationsSnapshotStatus {
  return (message as CallbackElaborationsSnapshotStatus).snapshot !== undefined;
}

function isSingleItem(message: CallbackMessage): message is CallbackElaborationsSnapshotStatus {
  return (message as CallbackSingleElaborationItemStatus).singleItem !== undefined;
}

function isElaborationStatus(message: CallbackMessage): message is CallbackElaborationStatus {
  return (message as CallbackElaborationStatus).items !== undefined;
}

const remoteElaborationsSlice = createSlice({
  name: "remoteElaborations",
  initialState,
  reducers: {
    removeElaborationItem: (state, action: PayloadAction<ElaborationStatusItem>) => {
      const event = action.payload;
      const previousList: ElaborationStatus[] = state.elaborationStatusList ?? [];
      state.elaborationStatusList = previousList.map((elab) => {
        elab.items = elab.items.filter((item) => item.id != event.id);
        return elab;
      });
      console.log("updated elaborations", state.elaborationStatusList);
    },
    flagElaborationItemResultAdded: (state: RemoteElaborationsState, action: PayloadAction<AddedElaborationItem>) => {
      state.elaborationStatusList = state.elaborationStatusList?.map((el) => {
        if (el.correlationId === action.payload.correlationId && el.items) {
          return {
            ...el,
            items: el.items.map((item) => (item.id === action.payload.itemId ? { ...item, resultAdded: true } : item)),
          };
        }
        return el;
      });
      console.log("flagElaborationResultAdded elaborations", state.elaborationStatusList, action.payload);
    },
    flagElaborationUserChecked: (state: RemoteElaborationsState, action: PayloadAction<{ correlationId: string }>) => {
      if (state.elaborationStatusList?.length > 0) {
        state.elaborationStatusList = state.elaborationStatusList.filter(
          (detail) => detail.correlationId !== action.payload.correlationId
        );
      }

      console.log("flagElaborationUserChecked elaborations", state.elaborationStatusList, action.payload);
    },
    resetStatusList: (state) => {
      state.elaborationStatusList = undefined;
      console.log("reset elaborations", state.elaborationStatusList);
    },
    updateElaboration: (
      state,
      action: PayloadAction<
        | CallbackElaborationStatus
        | CallbackElaborationsSnapshotStatus
        | CallbackSingleElaborationItemStatus
        | CallbackHeartbeatStatus
      >
    ) => {
      const event = action.payload;
      state.lastReceivedEventTimestampMs = new Date().getTime();
      if (isSnapshot(event)) {
        state.elaborationStatusList = event.snapshot;
        // Fix for missing correlationId
        state.elaborationStatusList.flatMap((e) => {
          e.items.forEach((i) => (i.correlationId = e.correlationId));
          return e.items;
        });
        console.log("snapshot elaborations", JSON.parse(JSON.stringify(state.elaborationStatusList)));
      } else if (isSingleItem(event)) {
        const receivedItem = event as CallbackSingleElaborationItemStatus;
        const previousList = state.elaborationStatusList ?? [];

        const thisElabIndex = previousList.findIndex((e) => e.correlationId === receivedItem.correlationId);
        if (thisElabIndex === -1) {
          state.elaborationStatusList = [
            ...previousList,
            {
              correlationId: receivedItem.correlationId,
              items: [receivedItem.singleItem],
            } as any,
          ];
          console.log(
            "updated elaborations with single item (created target)",
            JSON.parse(JSON.stringify(state.elaborationStatusList))
          );
          return;
        }

        const targetElab = previousList[thisElabIndex];
        const newItems = (targetElab.items ?? []).filter((i) => i.id !== receivedItem.singleItem.id);
        newItems.push(receivedItem.singleItem);

        // immutable replace-at-index
        state.elaborationStatusList = [
          ...previousList.slice(0, thisElabIndex),
          { ...targetElab, items: newItems },
          ...previousList.slice(thisElabIndex + 1),
        ];

        console.log("updated elaborations with single item", JSON.parse(JSON.stringify(state.elaborationStatusList)));
      } else if (isElaborationStatus(event)) {
        const receivedElaboration = event;
        // Fix for missing correlationId
        receivedElaboration?.items.forEach((e) => {
          e.correlationId = receivedElaboration.correlationId;
          return e;
        });
        const previousList = state.elaborationStatusList ?? [];
        const currentList = previousList.filter((e) => e.correlationId != receivedElaboration.correlationId);
        if (receivedElaboration.items.length) {
          //FIX IDS-1500 - An Empty Elaboration can be ignored end excluded from the list
          currentList.push(receivedElaboration);
        }
        state.elaborationStatusList = currentList;
        if (currentList?.length) state.hasElaboratedAtLeastOnce = true;
        console.log("updated elaborations with elaboration", JSON.parse(JSON.stringify(state.elaborationStatusList)));
      }
    },
    setHasElaboratedAtLeastOnce: (state) => {
      state.hasElaboratedAtLeastOnce = true;
    },
    setActiveEventChannelId: (state, action: PayloadAction<string>) => {
      state.activeEventChannelId = action.payload;
    },
  },
});

export const selectElaborationStatusList = (state: RootState) => state.remoteElaborations.elaborationStatusList;
export const selectActiveEventChannelId = (state: RootState) => state.remoteElaborations.activeEventChannelId;

export const selectElaborationsWithNotRunningAndNotCheckedItems = (state: RootState) =>
  (state.remoteElaborations.elaborationStatusList ?? []).filter((elaboration) =>
    elaboration.items.some((item) => elaborationStatusItemIsNotRunningAndNotUserChecked(item))
  );

export const selectElaborationsByCorrelationId = (state: RootState, correlationId: string) =>
  (state.remoteElaborations.elaborationStatusList ?? []).find(
    (elaboration) => elaboration?.correlationId === correlationId
  );

export const selectAbortedElaborations = (state: RootState) =>
  (state.remoteElaborations.elaborationStatusList ?? []).filter(
    (elaboration) =>
      elaboration.items.filter((item) => elaborationStatusItemIsAborted(item)).length == elaboration.items.length
  );

export const selectNotRunningAndNotUserCheckedElaborationStatusItemList = (state: RootState) =>
  (state.remoteElaborations.elaborationStatusList ?? [])
    .filter(
      (elaboration) =>
        elaboration.items.filter((item) => elaborationStatusItemIsNotRunningAndNotUserChecked(item)).length
    )
    .flatMap((elaboration) => elaboration.items);

export const {
  flagElaborationItemResultAdded,
  flagElaborationUserChecked,
  resetStatusList,
  updateElaboration,
  removeElaborationItem,
  setHasElaboratedAtLeastOnce,
  setActiveEventChannelId,
} = remoteElaborationsSlice.actions;

export default remoteElaborationsSlice.reducer;
